// src/pages/MyPublishedItems.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import { collection, query, getDocs, where, doc, getDoc } from 'firebase/firestore';
import useProtectedRoute from '../hooks/useProtectedRoute';
import Menu from '../components/Menu';
import H1 from '../components/H1/H1';
import Footer from '../components/Footer';
import MuralItemSkeleton from '../components/MuralItemSkeleton';
import MuralItem from "../components/MuralItem/MuralItem";
import MuralEvaluationPopup from '../components/MuralEvaluationPopup';
import { isExpired } from '../utils/muralExpiry';
import './AdsWall.css';

const ITEMS_PER_PAGE = 16;

const MyPublishedItems = () => {
  const navigate = useNavigate();
  const isLoggedIn = useProtectedRoute();
  const [publishedItems, setPublishedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState('ativos');
  const [archiveRequest, setArchiveRequest] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchUserData = async (userId) => {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      return userSnap.exists() ? userSnap.data() : {};
    };

    const fetchPublishedItems = async () => {
      const user = auth.currentUser;
      if (user) {
        const muralItemsRef = collection(db, 'mural');
        const q = query(muralItemsRef, where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const items = [];
        for (const docSnapshot of querySnapshot.docs) {
          const userData = await fetchUserData(docSnapshot.data().userId);
          items.push({
            id: docSnapshot.id,
            ...docSnapshot.data(),
            userName: userData.institutionName || userData.firstName,
            userAvatar: userData.avatar,
            userCity: userData.address?.city,
            userState: userData.address?.state,
          });
        }
        setPublishedItems(items);
        setIsLoading(false);
      }
    };

    if (isLoggedIn) {
      fetchPublishedItems();
    }
  }, [isLoggedIn, auth.currentUser]);

  const removeItemFromState = (itemId) => {
    setPublishedItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const handleArchived = (itemId, archived) => {
    setPublishedItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? {
              ...it,
              archivedAt: archived ? { seconds: Math.floor(Date.now() / 1000) } : null,
              archivedBy: archived ? (auth.currentUser ? auth.currentUser.uid : null) : null,
              isVisible: !archived,
            }
          : it
      )
    );
  };

  const filteredItems = useMemo(() => {
    // Vencidos (expiresAt no passado) são arquivados automaticamente: saem dos
    // ativos e aparecem em "Arquivados", mesmo sem o flag archivedAt gravado.
    if (tab === 'arquivados') {
      return publishedItems.filter((it) => Boolean(it.archivedAt) || isExpired(it));
    }
    return publishedItems.filter((it) => !it.archivedAt && !isExpired(it));
  }, [publishedItems, tab]);

  const tabButtonClass = (current) =>
    `px-4 py-2 font-bold border-b-2 ${tab === current ? 'border-purple-refugia text-purple-refugia' : 'border-transparent text-gray-500'}`;

  return (
    <>
      <Menu isLoggedIn={isLoggedIn} />
      <div className="ads-wall-container">
        <div className="row-container">
          <H1 text="Meus itens criados" bgColor="purple" fontColor="white" />
          <Link to="/mural-da-rede" className="back-to-mural-link">
            <span>&lt; Voltar para o mural</span>
          </Link>
        </div>
        <div className="flex gap-2 border-b border-gray-200 mb-4">
          <button type="button" className={tabButtonClass('ativos')} onClick={() => setTab('ativos')}>
            Ativos
          </button>
          <button type="button" className={tabButtonClass('arquivados')} onClick={() => setTab('arquivados')}>
            Arquivados
          </button>
        </div>
        <div className="ads-wall-grid">
          {isLoading ? (
            <MuralItemSkeleton count={ITEMS_PER_PAGE} />
          ) : filteredItems.length === 0 ? (
            <p className="text-gray-500">
              {tab === 'arquivados' ? 'Você ainda não tem itens arquivados.' : 'Você ainda não tem itens ativos.'}
            </p>
          ) : (
            filteredItems.map(item => (
              <MuralItem
                key={item.id}
                item={item}
                onToggleFavorite={() => {}}
                onNavigate={navigate}
                removeItemFromState={removeItemFromState}
                onArchiveRequest={setArchiveRequest}
                onArchived={handleArchived}
              />
            ))
          )}
        </div>
      </div>
      {archiveRequest && (
        <MuralEvaluationPopup
          item={archiveRequest.item}
          evaluatorId={auth.currentUser ? auth.currentUser.uid : null}
          onClose={() => setArchiveRequest(null)}
          onConfirm={async (evaluationPayload) => {
            try {
              await archiveRequest.finalize(evaluationPayload);
            } finally {
              setArchiveRequest(null);
            }
          }}
        />
      )}
      <Footer />
    </>
  );
};

export default MyPublishedItems;
